import React, { useState } from 'react';
import { BiPhone, BiUser, BiMapPin, BiMessage } from 'react-icons/bi';
import { BsTruck } from 'react-icons/bs';
import { Order } from '../types/orders';
import toast from 'react-hot-toast';

interface OrderTrackingInterfaceProps {
  order: Order;
  onClose: () => void;
}

const OrderTrackingInterface: React.FC<OrderTrackingInterfaceProps> = ({
  order,
  onClose
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [trackingStatus, setTrackingStatus] = useState('In-Progress');

  const handleCallCustomer = () => {
    toast.success('Calling customer...');
    // Implement call functionality
  };

  const handleCallRestaurant = () => {
    toast.success('Calling restaurant...');
    // Implement call functionality
  };

  const handleCallRider = () => {
    toast.success('Calling rider...');
    // Implement call functionality
  };

  const handleSendMessage = () => {
    toast.success('Opening message interface...');
    // Implement messaging functionality
  };

  const handleReportIssue = () => {
    toast.success('Opening issue reporting...');
    // Implement issue reporting functionality
  };

  const handleTrack = () => {
    if (trackingStatus === 'In-Progress') {
      setTrackingStatus('Closed');
      toast.success('Tracking closed');
    } else {
      setTrackingStatus('In-Progress');
      toast.success('Tracking resumed');
    }
  };

  const getTimelineStatus = (status: string) => {
    return order.timeline.find(t => t.status === status)?.completed || false;
  };

  const getTimelineTime = (status: string) => {
    return order.timeline.find(t => t.status === status)?.timestamp || '';
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold text-gray-900">
                    Order Tracking - {order.orderNumber}
                  </h1>
                  <span className="text-gray-400">{'>'}</span>
                  <span className="text-lg text-gray-600">Order Tracking</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Real-time order tracking and management
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                trackingStatus === 'In-Progress' 
                  ? 'bg-purple-100 text-purple-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {trackingStatus}
              </span>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close Tracking
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Communication Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={handleCallCustomer}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiPhone className="w-4 h-4" />
            Call Customer
          </button>
          <button
            onClick={handleCallRestaurant}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiPhone className="w-4 h-4" />
            Call Restaurant
          </button>
          <button
            onClick={handleCallRider}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiPhone className="w-4 h-4" />
            Call Rider
          </button>
          <button
            onClick={handleSendMessage}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiMessage className="w-4 h-4" />
            Send Message
          </button>
          <button
            onClick={handleReportIssue}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Report Issue
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Map and Customer/Restaurant Info */}
          <div className="space-y-6">
            {/* Live Tracking Map */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
                <BiMapPin className="w-5 h-5 text-teal-600" />
                <h2 className="text-lg font-semibold text-gray-900">Live Tracking Map</h2>
                <p className="text-sm text-gray-500 ml-auto">Real-time location of rider and delivery route</p>
              </div>
              
              {/* Mock Map Area */}
              <div className="relative bg-gradient-to-br from-green-100 to-blue-100 h-80">
                {/* Mock map elements */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/90 p-4 rounded-lg shadow-lg max-w-sm text-center">
                    <div className="flex items-center justify-center gap-4 mb-4">
                      {/* Pickup location */}
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mb-2">
                          <BsTruck className="w-4 h-4 text-orange-600" />
                        </div>
                        <span className="text-xs text-gray-600">Tasty Place</span>
                      </div>
                      
                      {/* Route line */}
                      <div className="flex-1 h-0.5 bg-gray-300 relative">
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                          <div className="w-4 h-4 bg-green-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">R</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Delivery location */}
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mb-2">
                          <BiMapPin className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-xs text-gray-600">Delivery Address</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span className="text-blue-600">15 mins</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                        <span className="text-gray-600">2.2KM</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer and Restaurant Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Customer Information:</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <BiUser className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{order.customer.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BiPhone className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{order.customer.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BiMapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{order.customer.address}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Restaurant Information:</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <BsTruck className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{order.restaurant.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BiPhone className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{order.restaurant.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BiMapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{order.restaurant.address}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Timeline and Order Items */}
          <div className="space-y-6">
            {/* Order Timeline */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <BiMapPin className="w-5 h-5 text-teal-600" />
                <h2 className="text-lg font-semibold text-gray-900">Order Timeline</h2>
                <p className="text-sm text-gray-500 ml-auto">Detailed progress of your order</p>
              </div>
              
              <div className="space-y-4">
                {/* Order Placed */}
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    getTimelineStatus('pending') ? 'bg-teal-600' : 'bg-gray-200'
                  }`}>
                    {getTimelineStatus('pending') && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">Order Placed</h4>
                        <p className="text-sm text-gray-600">Order received by vendor</p>
                      </div>
                      <span className="text-sm text-gray-500">{getTimelineTime('pending') || '02:20pm'}</span>
                    </div>
                  </div>
                </div>

                {/* Order Confirmed */}
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    getTimelineStatus('confirmed') ? 'bg-teal-600' : 'bg-gray-200'
                  }`}>
                    {getTimelineStatus('confirmed') && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">Order Confirmed</h4>
                        <p className="text-sm text-gray-600">Vendor accepted the order</p>
                      </div>
                      <span className="text-sm text-gray-500">{getTimelineTime('confirmed') || '02:20pm'}</span>
                    </div>
                  </div>
                </div>

                {/* Preparing */}
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    getTimelineStatus('preparing') ? 'bg-teal-600' : 'bg-gray-200'
                  }`}>
                    {getTimelineStatus('preparing') && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">Preparing</h4>
                        <p className="text-sm text-gray-600">Food is being prepared</p>
                      </div>
                      <span className="text-sm text-gray-500">{getTimelineTime('preparing') || '02:20pm'}</span>
                    </div>
                  </div>
                </div>

                {/* Ready For Pickup */}
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    getTimelineStatus('ready') ? 'bg-teal-600' : 'bg-gray-200'
                  }`}>
                    {getTimelineStatus('ready') && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">Ready For Pickup</h4>
                        <p className="text-sm text-gray-600">Order ready for pickup</p>
                      </div>
                      <span className="text-sm text-gray-500">{getTimelineTime('ready') || '02:20pm'}</span>
                    </div>
                  </div>
                </div>

                {/* Picked Up */}
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    getTimelineStatus('picked_up') ? 'bg-teal-600' : 'bg-gray-200'
                  }`}>
                    {getTimelineStatus('picked_up') && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">Picked Up</h4>
                        <p className="text-sm text-gray-600">Rider picked up the order</p>
                      </div>
                      <span className="text-sm text-gray-500">{getTimelineTime('picked_up') || '02:20pm'}</span>
                    </div>
                  </div>
                </div>

                {/* Out for Delivery */}
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    getTimelineStatus('out_for_delivery') ? 'bg-teal-600' : 'bg-purple-600'
                  }`}>
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-purple-600">Out for Delivery</h4>
                        <p className="text-sm text-purple-600">On the way to customer</p>
                      </div>
                      <span className="text-sm text-gray-500"></span>
                    </div>
                  </div>
                </div>

                {/* Delivered */}
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    getTimelineStatus('delivered') ? 'bg-teal-600' : 'bg-gray-200'
                  }`}>
                    {getTimelineStatus('delivered') && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">Delivered</h4>
                        <p className="text-sm text-gray-600">Order delivered to customer</p>
                      </div>
                      <span className="text-sm text-gray-500">{getTimelineTime('delivered')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Order Items:</h3>
              <div className="space-y-3">
                {order.items.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                    <span className="text-sm text-gray-900">{item.name} x{item.quantity}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-4 border-t mt-4">
                <h3 className="text-lg font-semibold text-teal-600">Total:</h3>
                <span className="text-xl font-bold text-teal-600">₦{order.total.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center pt-6">
          <button
            onClick={handleTrack}
            className={`px-8 py-3 rounded-lg font-medium transition-colors ${
              trackingStatus === 'In-Progress'
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
          >
            {trackingStatus === 'In-Progress' ? 'Close Tracking' : 'Track'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingInterface;